'use client';

import { useLayoutEffect, useMemo, useRef, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { LinkWrapper } from '@/components/link';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { resolveSeoLinksVisibleCount } from '@/components/seo-linking/count-seo-links-first-row';
import { measureSeoLinkWidths } from '@/components/seo-linking/measure-seo-link-widths';
import type { SeoLink } from '@/lib/seo-linking/resolve-links';
import { cn } from '@/lib/utils';
import { focusVisibleOutline } from 'src/lib/focus-styles';

const OVERFLOW_ACCORDION_VALUE = 'seo-links-overflow';

export type SeoLinkingSectionProps = {
    title: string;
    links: SeoLink[];
    className?: string;
    navAriaLabel?: string;
};

export function SeoLinkItem({ item }: { item: SeoLink }): ReactElement | null {
    return (
        <li className="shrink-0 whitespace-nowrap">
            <LinkWrapper to={item.to} className={cn('body-copy-l-bold', focusVisibleOutline)} aria-label={item.name}>
                {item.name}
            </LinkWrapper>
        </li>
    );
}

/**
 * Presentational SEO internal-linking section: label title and crawlable category links.
 * Visible count is measurement-only (available row width vs link widths); overflow uses a +/- accordion.
 * Layout: stacked on small viewports; md+ title and links share one row (`md:gap-8`).
 */
export default function SeoLinkingSection({
    title,
    links,
    className,
    navAriaLabel,
}: SeoLinkingSectionProps): ReactElement | null {
    const { t } = useTranslation('seoLinking');
    const navRef = useRef<HTMLElement>(null);
    const rowRef = useRef<HTMLDivElement>(null);
    const [visibleCount, setVisibleCount] = useState(links.length);
    const [accordionValue, setAccordionValue] = useState('');

    const linksKey = useMemo(() => links.map((link) => link.id).join('\u001f'), [links]);
    const linkNames = useMemo(() => links.map((link) => link.name), [links]);
    const n = links.length;
    const isExpanded = accordionValue === OVERFLOW_ACCORDION_VALUE;

    useLayoutEffect(() => {
        if (typeof window === 'undefined' || n === 0) {
            setVisibleCount(0);
            return;
        }

        const updateVisibleCount = () => {
            const nav = navRef.current;
            const row = rowRef.current;
            if (!nav || !row) return;

            const widths = measureSeoLinkWidths(linkNames, nav);
            setVisibleCount(resolveSeoLinksVisibleCount(row.getBoundingClientRect().width, widths));
        };

        updateVisibleCount();

        const row = rowRef.current;
        if (!row) return;

        const ro = new ResizeObserver(() => {
            updateVisibleCount();
        });
        ro.observe(row);

        return () => {
            ro.disconnect();
        };
    }, [n, linksKey, linkNames]);

    useLayoutEffect(() => {
        setAccordionValue('');
    }, [linksKey, visibleCount]);

    const hasOverflow = visibleCount < n;
    const visibleLinks = links.slice(0, visibleCount);
    const hiddenLinks = links.slice(visibleCount);
    const hiddenNames = hiddenLinks.map((link) => link.name).join(', ');
    const toggleAriaLabel = isExpanded
        ? t('collapseLinks', { names: hiddenNames })
        : t('expandLinks', { count: hiddenLinks.length, names: hiddenNames });

    if (links.length === 0) {
        return null;
    }

    return (
        <section className={cn('bg-neutral-white', className)}>
            <div className="grid-global py-6">
                <div
                    className={cn(
                        'col-span-full flex flex-col gap-4 md:flex-row md:gap-8 lg:col-span-10 lg:col-start-2',
                        hasOverflow ? 'md:items-baseline' : 'md:items-center'
                    )}>
                    <h2 className="label-l shrink-0 text-neutral-40">{title}</h2>
                    <nav ref={navRef} aria-label={navAriaLabel ?? title} className="relative min-w-0 flex-1">
                        {hasOverflow ? (
                            <Accordion
                                type="single"
                                collapsible
                                value={accordionValue}
                                onValueChange={setAccordionValue}>
                                <AccordionItem value={OVERFLOW_ACCORDION_VALUE} className="border-0">
                                    <div
                                        ref={rowRef}
                                        className="flex w-full min-w-0 items-center justify-between gap-4">
                                        <ul className="flex min-w-0 flex-1 flex-nowrap gap-x-6 overflow-hidden">
                                            {visibleLinks.map((item) => (
                                                <SeoLinkItem key={item.id} item={item} />
                                            ))}
                                        </ul>
                                        <AccordionTrigger
                                            className={cn(
                                                'shrink-0 !flex-none !py-0 !pb-0',
                                                'h-auto min-h-0 w-auto gap-0 hover:no-underline'
                                            )}>
                                            <span className="sr-only">{toggleAriaLabel}</span>
                                        </AccordionTrigger>
                                    </div>
                                    <AccordionContent className="p-0 pt-2 pb-0">
                                        <ul className="flex flex-row flex-wrap gap-x-6 gap-y-2">
                                            {hiddenLinks.map((item) => (
                                                <SeoLinkItem key={item.id} item={item} />
                                            ))}
                                        </ul>
                                    </AccordionContent>
                                </AccordionItem>
                            </Accordion>
                        ) : (
                            <div ref={rowRef} className="flex w-full min-w-0 items-center justify-between gap-4">
                                <ul className="flex min-w-0 flex-1 flex-nowrap gap-x-6 overflow-hidden">
                                    {visibleLinks.map((item) => (
                                        <SeoLinkItem key={item.id} item={item} />
                                    ))}
                                </ul>
                            </div>
                        )}
                    </nav>
                </div>
            </div>
        </section>
    );
}
